import React, { createContext, useContext } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MyGatesPage from './residential/MyGatesPage';
import ResidentialGateSettings from './residential/ResidentialGateSettings';
import ResidentialProfile from './residential/ResidentialProfile';
import ResidentialFamilyPage from './residential/ResidentialFamilyPage';
import { IconUsers, IconArrowRight } from '../components/Icons';
import '../residential.css';

/**
 * Phase 5.D — residential customer shell.
 *
 * Lives at /r/* and replaces the industrial CustomerHome for any
 * customer whose ``customer_type === 'residential'``. The shell is
 * intentionally minimal: a single sticky header with the page title
 * and a profile button, and a routed content area for the few
 * screens a residential customer ever needs (my gates → gate
 * settings → profile).
 *
 * The same component is also mounted at /r/preview/* for Huri staff
 * (super_admin) to QA the look without a real residential customer
 * in the DB. ``basePath`` (defaults to ``/r``) lets every internal
 * link know which prefix to navigate under.
 */

const BasePathContext = createContext('/r');
export const useResidentialBasePath = () => useContext(BasePathContext);

function ResidentialHome({ user, setUser, basePath = '/r' }) {
  return (
    <BasePathContext.Provider value={basePath}>
      <div className="r-app">
        <Routes>
          <Route path="/" element={<MyGatesPage user={user} />} />
          <Route
            path="/gates/:gateId/settings"
            element={<ResidentialGateSettings />}
          />
          <Route
            path="/profile"
            element={<ResidentialProfile user={user} setUser={setUser} />}
          />
          <Route
            path="/family"
            element={<ResidentialFamilyPage user={user} />}
          />
        </Routes>
      </div>
    </BasePathContext.Provider>
  );
}

/**
 * Shared header rendered by each residential sub-page so the layout
 * stays uniform. Includes the Huri Connect brand mark + name on the
 * top row, then a large title row with optional back button.
 */
export function ResidentialHeader({ title, subtitle, backTo, onProfileClick }) {
  const navigate = useNavigate();
  const handleBack = () => navigate(backTo);
  return (
    <header className="r-header">
      <div className="r-header-row">
        <div className="r-brand">
          <span className="r-brand-mark" aria-hidden="true">H</span>
          <span>Huri Connect</span>
        </div>
        {backTo !== undefined ? (
          <button
            type="button"
            className="r-header-btn"
            onClick={handleBack}
            aria-label="חזרה"
          >
            <IconArrowRight size={18} />
          </button>
        ) : (
          <button
            type="button"
            className="r-header-btn"
            onClick={onProfileClick}
            aria-label="פרופיל"
          >
            <IconUsers size={18} />
          </button>
        )}
      </div>
      <div className="r-header-title">{title}</div>
      {subtitle && <div className="r-header-subtitle">{subtitle}</div>}
    </header>
  );
}

export async function residentialLogout(setUser) {
  await axios.post('/api/auth/logout');
  setUser(null);
}

export default ResidentialHome;
